import { X, SendHorizontal, Keyboard, Languages } from "lucide-react";
import { Presence } from "@/components/Presence";
import { MemoryRail } from "@/components/SourceRail";
import { Console } from "@/components/session/Console";

export const SessionDesktop = ({ state, labName, accent = "#C87D55", mode, setMode, turns, draft, setDraft, onSend, onMic, end, cycleLang, langLabel, openings, ask, convo, toggleConvo, cancelRecording, elapsed, showTranscript, setSource, bottom, busy, aiConfigured, optIn, read, toggleRead, passages, togglePassages, sourcesAvailable, stopSpeaking }) => {
  const typing = mode === "text";   // a typed sitting: no mic, no voice, his reply in writing
  const started = turns.length > 0;
  const asked = turns.filter((t) => t.role === "user").length;

  return (
    <div
      className="grain lamp relative flex h-screen overflow-hidden"
      style={{ "--lab-accent": accent, background: "var(--bg-primary)" }}
      data-testid="session-screen"
    >
      <main className="relative z-10 flex min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-start justify-between px-14 pb-4 pt-8">
          <div className="flex items-center gap-4">
            <span className="h-11 w-[2px] rounded-full" style={{ background: `linear-gradient(180deg, transparent, ${accent}, transparent)` }} />
            <div>
              <p className="eyebrow" data-testid="session-lab-name">{labName}</p>
              <p className="mt-1.5 font-mono text-[10px] tracking-wider" style={{ color: "var(--text-faint)" }}>
                anonymous · {optIn ? "kept, anonymised" : "nothing kept"}{started ? ` · ${asked} asked` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="language-toggle-btn"
              onClick={cycleLang}
              title="Change language"
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors hover:text-[#F4EFE6]"
              style={{ borderColor: "var(--border-linen)", color: "var(--text-dim)" }}
            >
              <Languages size={13} style={{ color: accent }} />
              <span data-testid="language-toggle-value">{langLabel.label}</span>
            </button>
            <button
              data-testid="end-session-btn"
              onClick={end}
              className="inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors hover:text-[#F4EFE6]"
              style={{ borderColor: "var(--border-linen)", color: "var(--text-faint)" }}
            >
              <X size={13} /> leave
            </button>
          </div>
        </header>

        {!aiConfigured && (
          <p data-testid="ai-not-configured-banner" className="mx-14 mb-2 rounded-2xl px-4 py-2.5 text-xs" style={{ border: "1px solid rgba(212,163,89,0.3)", background: "rgba(212,163,89,0.08)", color: "#D4A359" }}>
            My voice isn't connected yet — a curator still needs to add a key.
          </p>
        )}

        <div className="flex min-h-0 flex-1 flex-col justify-center px-14 2xl:px-20">
          <div className="mx-auto w-full max-w-[820px]">
            <Presence state={state} accent={accent} height={started ? 260 : 310} />

            {!started && (
              <div className="rise rise-2 mt-6 max-w-[560px]">
                <h1 className="font-display text-[44px] font-light leading-[1.08] tracking-tight" style={{ color: "var(--text-main)" }}>
                  What stayed<br />with you?
                </h1>
                <p className="mt-5 max-w-[27rem] text-[14px] leading-[1.85]" style={{ color: "var(--text-dim)" }}>
                  Speak freely. I keep what this Lab has said, made and felt — and I'll hand it back to you with its source, never anything invented.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 px-14 pb-10 pt-10 2xl:px-20">
          <div className={`mx-auto flex w-full max-w-[820px] items-center gap-8 ${typing ? "justify-center" : "justify-between"}`}>
            {!typing && (
            <Console
              state={state}
              convo={convo}
              toggleConvo={toggleConvo}
              onMic={onMic}
              cancelRecording={cancelRecording}
              elapsed={elapsed}
              busy={busy}
              read={read}
              toggleRead={toggleRead}
              passages={passages}
              togglePassages={togglePassages}
              sourcesAvailable={sourcesAvailable}
              stopSpeaking={stopSpeaking}
            />
            )}

            <div className="pb-0">
              {typing ? (
                <div className="glass flex w-[min(560px,48vw)] items-center gap-2 rounded-full p-1.5 pl-5">
                  <input
                    autoFocus
                    data-testid="text-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSend();
                      if (e.key === "Escape") { setDraft(""); setMode("voice"); }
                    }}
                    placeholder="Write to Griot…"
                    className="min-w-0 flex-1 bg-transparent text-[14px] focus:outline-none"
                    style={{ color: "var(--text-main)" }}
                  />
                  <button data-testid="text-send-btn" onClick={onSend} disabled={busy || !draft.trim()} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-opacity disabled:opacity-40" style={{ backgroundColor: accent, color: "#0C0C0B" }}>
                    <SendHorizontal size={15} />
                  </button>
                  <button
                    data-testid="text-close-btn"
                    onClick={() => { setDraft(""); setMode("voice"); }}
                    aria-label="Hide the keyboard and go back to speaking"
                    title="Back to speaking"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors hover:text-[#F4EFE6]"
                    style={{ borderColor: "var(--border-linen)", color: "var(--text-faint)" }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  data-testid="type-instead-btn"
                  onClick={() => setMode("text")}
                  className="inline-flex items-center gap-2 whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.22em] transition-colors hover:text-[#F4EFE6]"
                  style={{ color: "var(--text-faint)" }}
                >
                  <Keyboard size={12} /> or write instead
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      <MemoryRail
        accent={accent}
        turns={turns}
        showTranscript={showTranscript}
        showPassages={passages && sourcesAvailable}
        openings={openings}
        ask={ask}
        onOpen={setSource}
        state={state}
        bottom={bottom}
      />
    </div>
  );
};
